import { h, RefObject } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useBlackBarSizeDetector } from '../../common/creative-validators';
import { useAdParameters } from '../../common/creative/hooks/useAdParameters';
import { useAdSize } from '../../common/creative/hooks/useAdSize';
import { useCTAColor } from '../../common/creative/hooks/useCTAColor';
import { useCustomizePlayer } from '../../common/creative/hooks/useCustomizePlayer';
import { cssBlock, useDynamicCSS } from '../../common/creative/hooks/useDynamicCSS';
import { useMaxElementHeight } from '../../common/creative/hooks/useMaxElementHeight';
import { useMoveVideoSlotInContainer } from '../../common/creative/hooks/useMoveVideoSlotInContainer';
import { useOptimizedAsset } from '../../common/creative/hooks/useOptimizedAsset';
import { useResizeObserver } from '../../common/creative/hooks/useResizeObserver';
import { useSetVideoSlotURI } from '../../common/creative/hooks/useSetVideoSlotURI';
import { useShowEndScreen } from '../../common/creative/hooks/useShowEndScreen';
import { useTextKeywords } from '../../common/creative/hooks/useTextKeywords';
import { useUpdateSlotSizeWithVideoSlot } from '../../common/creative/hooks/useUpdateSlotSize';
import { Arrow } from '../../common/creative/icons/Arrow';
import { ImageCropWrapper } from '../../common/creative/image-crop-wrapper';
import { getLocaleForLanguage } from '../../common/creative/locales';
import { isRtlLanguage } from '../../common/creative/rtl-languages';
import { AssetComponentId } from '../../common/creative/social/asset-component';
import { CreativeComponent } from '../../common/creative/social/creative-component';
import { style } from '../../common/creative/social/lib';
import { LineClamp } from '../../common/creative/social/line-clamp';
import { classNames } from '../../common/css';
import { PerformanceSingleVideoConfig } from './config';
import classes from './creative.scss';
import { Layout, LayoutMap } from './layout';

const BorderWidth: LayoutMap = {
  [Layout.Mobile]: 0,
  [Layout.Desktop]: 1,
  [Layout.MPUOneLine]: 1,
  [Layout.MPUTwoLines]: 1,
  [Layout.Unknown]: 0,
};

const TitleLineHeight: LayoutMap = {
  [Layout.Mobile]: 22,
  [Layout.Desktop]: 22,
  [Layout.MPUOneLine]: 17,
  [Layout.MPUTwoLines]: 17,
  [Layout.Unknown]: 22,
};

const DescriptionLineHeight = 19;

const LayoutClassName: LayoutMap<string> = {
  [Layout.Desktop]: classes.layoutDesktop,
  [Layout.Mobile]: classes.layoutMobile,
  [Layout.MPUOneLine]: classes.layoutMpuOneLine,
  [Layout.MPUTwoLines]: classes.layoutMpuTwoLines,
  [Layout.Unknown]: '',
};

function useCSSForCTA(color: string | undefined) {
  const [normalCTAColor, hoverCTAColor] = useCTAColor(color);
  const setCTAColor = useDynamicCSS();
  useEffect(() => {
    const css = cssBlock(
      `.${classes.CTA} {color: ${normalCTAColor};}`,
      `.${classes.active} .${classes.CTA} {background-color: ${normalCTAColor};}`,
      `.${classes.layoutDesktop} .${classes.CTA}:hover {background-color: ${hoverCTAColor};}`,
      `.${classes.CTAArrow} {color: ${normalCTAColor}}`
    );

    setCTAColor(css);
  }, [normalCTAColor, hoverCTAColor]);
}

const isRTL = (c: PerformanceSingleVideoConfig) => isRtlLanguage(c.ctaLanguage);

export interface PerformanceSingleImageCreativeProps {
  onClick?: (evt: MouseEvent) => void;
  isCtaActive: boolean;
  className?: string;
  videoRatio: number;
  videoslot: HTMLVideoElement;
  layout: Layout;
  titleRef?: RefObject<HTMLDivElement>;
}

interface VideoContainerProps {
  videoslot: HTMLVideoElement;
  videoRatio: number;
  style?: any;
  children?: any;
}

function VideoContainer({ videoslot, videoRatio, style, children }: VideoContainerProps) {
  const adSize = useAdSize();
  const videoSlotContainerRef = useRef<HTMLDivElement>(null);
  const videoSlotContainerSize = useResizeObserver(videoSlotContainerRef.current);
  useMaxElementHeight(videoSlotContainerRef.current, videoRatio);
  useMoveVideoSlotInContainer(adSize, videoSlotContainerRef, videoslot, videoRatio);
  useCustomizePlayer(adSize, videoSlotContainerRef);
  useBlackBarSizeDetector(
    videoSlotContainerSize.width,
    videoSlotContainerSize.height,
    videoslot?.videoWidth ?? 0,
    videoslot?.videoHeight ?? 0
  );
  return (
    <div id={AssetComponentId} ref={videoSlotContainerRef} className={classes.Video} style={style}>
      {children}
    </div>
  );
}

export function PerformanceSingleVideoCreative({
  videoslot,
  videoRatio,
  onClick,
  isCtaActive,
  className,
  layout,
  titleRef,
}: PerformanceSingleImageCreativeProps) {
  const creativeClasses = useMemo(
    () => classNames(className, classes.Creative, LayoutClassName[layout], isCtaActive && classes.active),
    [isCtaActive, layout]
  );

  const { config } = useAdParameters<PerformanceSingleVideoConfig>();
  const showEndScreen = useShowEndScreen();

  const adSize = useAdSize();
  const containerSize = useMemo(
    () => ({ width: adSize.width, height: adSize.width / videoRatio }),
    [adSize, videoRatio]
  );
  const mainVideoUri = useOptimizedAsset(config.main_video);

  const showDescription = layout === Layout.Mobile && !!config.description;
  const showCtaText = layout !== Layout.MPUTwoLines;
  useSetVideoSlotURI(mainVideoUri, videoslot);

  const locale = getLocaleForLanguage(config.ctaLanguage);

  const title = useTextKeywords(config.title, locale);
  const description = useTextKeywords(config.description || '', locale);

  useCSSForCTA(config.ctaColor);

  const adRef = useRef<HTMLElement>(null);
  useUpdateSlotSizeWithVideoSlot(adRef.current, videoslot);

  useEffect(() => {
    if (showEndScreen) videoslot?.pause?.();
    style(videoslot, {
      visibility: showEndScreen ? 'hidden' : 'visible',
    });
  }, [showEndScreen]);

  return (
    <CreativeComponent<PerformanceSingleVideoConfig>
      adRef={adRef}
      onClick={onClick}
      className={creativeClasses}
      isRTL={isRTL}
    >
      <VideoContainer videoslot={videoslot} videoRatio={videoRatio} style={containerSize}>
        {showEndScreen && <ImageCropWrapper className={classes.EndScreen} alt="end-screen" asset={config.end_screen} />}
      </VideoContainer>
      <div className={classes.Footer}>
        <div className={classes.TextBox}>
          <div className={classes.Brand}>
            {config.logo && config.logo.uri ? (
              <ImageCropWrapper asset={config.logo} className={classes.Logo} alt="" />
            ) : null}
            <span className={classes.BrandName}>{config.brand}</span>
            <span className={classes.Sponsored}>
              {'· '}
              {config.sponsored}
            </span>
          </div>
          <div className={classes.Title}>
            <LineClamp
              lines={2}
              lineHeight={TitleLineHeight[layout]}
              text={title}
              containerRef={titleRef}
              dots
              keywords
            />
          </div>
          {showDescription && (
            <div className={classes.Description}>
              <LineClamp lines={3} lineHeight={DescriptionLineHeight} text={description} dots keywords />
            </div>
          )}
        </div>
        <div className={classes.CTABox}>
          <div className={classes.CTA}>
            {showCtaText && <span className={classes.CTATextContainer}>{config.cta}</span>}
            <Arrow className={classes.CTAArrow} />
          </div>
        </div>
      </div>
    </CreativeComponent>
  );
}
